import { Injectable } from '@angular/core';
import { Observable, fromEvent, merge, Subject, timer, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

// code inspired by https://github.com/bearnithi/bn-ng-idle
@Injectable({
  providedIn: 'root'
})
export class IdleService {

  private idle$!: Observable<any>;
  private timer$!: Subscription;
  private timeOutMilliSeconds!: number;
  private idleSubscription!: Subscription;

  private readonly ACTIVITY_CHANNEL = 'IdleService-Activity';
  private readonly TIMEOUT_CHANNEL = 'IdleService-Timeout';

  private activityBroadcast: BroadcastChannel;
  private timeoutBroadcast: BroadcastChannel;
  private lastActivityDate: Date;

  public expired$: Subject<void> = new Subject<void>();

  constructor() {
    this.activityBroadcast = new BroadcastChannel(this.ACTIVITY_CHANNEL);
    this.timeoutBroadcast = new BroadcastChannel(this.TIMEOUT_CHANNEL);
  }

  public start(timeOutMinutes: number): Observable<any> {
    if (typeof timeOutMinutes !== 'number' || isNaN(timeOutMinutes) || timeOutMinutes <= 0) {
      throw new Error(`Invalid timeout value: ${timeOutMinutes}`);
    }

    this.timeOutMilliSeconds = timeOutMinutes * 60 * 1000;
    this.lastActivityDate = new Date();

    this.timeoutBroadcast.onmessage = () => {
      console.log('IdleService inactivity detected on another tab. Trigger timeout.');
      this.expired$.next();
    };

    this.activityBroadcast.onmessage = (event) => {
      console.log('IdleService activity detected on another tab. Restart timer.');
      this.lastActivityDate = event.data.date;
    };

    this.idle$ = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'click'),
      fromEvent(document, 'mousedown'),
      fromEvent(document, 'keypress'),
      fromEvent(document, 'DOMMouseScroll'),
      fromEvent(document, 'mousewheel'),
      fromEvent(document, 'touchmove'),
      fromEvent(document, 'MSPointerMove'),
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'visible')
      ),
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'resize'),
    ).pipe(
      debounceTime(1000)
    );

    this.idleSubscription = this.idle$.subscribe(() => {
      this.lastActivityDate = new Date();
      this.activityBroadcast.postMessage({ date: this.lastActivityDate });
    });

    this.startTimer();

    return this.expired$;
  }

  public stop() {
    // console.log('IdleService stop called');
    if (this.timer$) {
      this.timer$.unsubscribe();
    }

    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
    }

    this.activityBroadcast.close();
    this.timeoutBroadcast.close();
  }

  private startTimer() {
    if (this.timer$) {
      this.timer$.unsubscribe();
    }

    console.log(`IdleService startTimer called with ${this.timeOutMilliSeconds} ms`, new Date().toLocaleString());

    this.timer$ = timer(this.timeOutMilliSeconds).subscribe(() => {

      // in case broadcastchannel message missed
      // browser throttle, tab suspend
      const lastActivity = this.lastActivityDate.getTime();
      const now = Date.now();
      if ((now - lastActivity) >= this.timeOutMilliSeconds) {
        console.log(`IdleService timer elapsed and inactivity detected. Last activity detected on ${this.lastActivityDate.toLocaleString()}`);
        this.timeoutBroadcast.postMessage(null);
        this.expired$.next();
      }
      else {
        console.log(`IdleService timer elapsed but activity detected on ${this.lastActivityDate.toLocaleString()}. Will restart timer`);
        this.startTimer();
      }
    });
  }
}
